// Shared sorting/grouping utilities for inventory items, shopping list items,
// and the recipe ingredient picker. All three share the same shape (item_name,
// category, created_at), so a single generic implementation covers all of them.

import { CATEGORIES } from "./categories";

export type SortMode = "alphabetical" | "category" | "date";
export type SortDirection = "asc" | "desc";

export interface SortPreference {
  mode: SortMode;
  // Only meaningful when mode === "date", but kept on the object so the last
  // direction used is remembered when switching away from and back to "date".
  dateDirection: SortDirection;
}

// Matches today's default ordering (newest first) so nothing changes visually
// for users who don't touch the sort control.
export const DEFAULT_SORT_PREFERENCE: SortPreference = {
  mode: "category",
  dateDirection: "desc",
};

function isSortPreference(value: unknown): value is SortPreference {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Record<string, unknown>;
  return (
    (candidate.mode === "alphabetical" ||
      candidate.mode === "category" ||
      candidate.mode === "date") &&
    (candidate.dateDirection === "asc" || candidate.dateDirection === "desc")
  );
}

// Read a persisted sort preference from localStorage, falling back to the
// default if nothing is stored or the stored value is malformed.
export function loadSortPreference(storageKey: string): SortPreference {
  try {
    const stored = localStorage.getItem(storageKey);
    if (!stored) return DEFAULT_SORT_PREFERENCE;
    const parsed: unknown = JSON.parse(stored);
    return isSortPreference(parsed) ? parsed : DEFAULT_SORT_PREFERENCE;
  } catch {
    return DEFAULT_SORT_PREFERENCE;
  }
}

export function saveSortPreference(
  storageKey: string,
  pref: SortPreference,
): void {
  try {
    localStorage.setItem(storageKey, JSON.stringify(pref));
  } catch {
    // Ignore storage errors (e.g. private browsing quota exceeded)
  }
}

export interface Sortable {
  item_name: string;
  category?: string | null;
  created_at: string;
}

// Sort a flat list for the "alphabetical" and "date" modes. "category" mode
// doesn't reorder the flat list itself; use groupByCategory for that instead.
export function sortItems<T extends Sortable>(
  items: T[],
  pref: SortPreference,
): T[] {
  const sorted = [...items];

  if (pref.mode === "alphabetical") {
    sorted.sort((a, b) =>
      a.item_name.localeCompare(b.item_name, undefined, {
        sensitivity: "base",
      }),
    );
    return sorted;
  }

  if (pref.mode === "date") {
    sorted.sort((a, b) => {
      const diff =
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      return pref.dateDirection === "asc" ? diff : -diff;
    });
    return sorted;
  }

  return sorted;
}

export interface CategoryGroup<T> {
  category: string;
  items: T[];
}

// Group items by category for the "By Category" sort mode. Groups follow the
// canonical CATEGORIES order, with an "Uncategorized" group (items with no or
// empty category) appended last. Items within each group are alphabetized.
export function groupByCategory<T extends Sortable>(
  items: T[],
): CategoryGroup<T>[] {
  const byCategory = new Map<string, T[]>();
  const uncategorized: T[] = [];

  for (const item of items) {
    const cat = item.category?.trim();
    if (!cat) {
      uncategorized.push(item);
      continue;
    }
    const existing = byCategory.get(cat);
    if (existing) {
      existing.push(item);
    } else {
      byCategory.set(cat, [item]);
    }
  }

  const alphabetize = (group: T[]) =>
    [...group].sort((a, b) =>
      a.item_name.localeCompare(b.item_name, undefined, {
        sensitivity: "base",
      }),
    );

  const groups: CategoryGroup<T>[] = [];

  for (const cat of CATEGORIES) {
    const group = byCategory.get(cat);
    if (group && group.length > 0) {
      groups.push({ category: cat, items: alphabetize(group) });
      byCategory.delete(cat);
    }
  }

  // Defensive: any category present in the data but not in the canonical list
  // (e.g. stale data) still gets shown, appended alphabetically by name.
  const leftovers = [...byCategory.entries()].sort(([a], [b]) =>
    a.localeCompare(b),
  );
  for (const [cat, group] of leftovers) {
    groups.push({ category: cat, items: alphabetize(group) });
  }

  if (uncategorized.length > 0) {
    groups.push({
      category: "Uncategorized",
      items: alphabetize(uncategorized),
    });
  }

  return groups;
}
